import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Modal, Pressable, SafeAreaView } from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { ResearchReport } from '@components/research/ResearchReport';
import { FlowDiagram } from '@components/research/FlowDiagram';
import { researchStorageService } from '@services/research/researchStorage';
import { ResearchSession, AgentStepType } from '@/types/research.types';
import { ResearchStackParamList } from '@/navigation/types';
import { MarkdownText } from '@components/common/MarkdownText';
import { ScrollColumn } from '@components/common/ScrollColumn';

type DetailRoute = RouteProp<ResearchStackParamList, 'ResearchHistoryDetail'>;

export const ResearchHistoryDetailScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<DetailRoute>();
  const { sessionId } = route.params;
  const [session, setSession] = useState<ResearchSession | null>(null);
  const [expandedStep, setExpandedStep] = useState<AgentStepType | null>(null);
  const [outputModal, setOutputModal] = useState<{ visible: boolean; title: string; content: string }>({
    visible: false,
    title: '',
    content: '',
  });
  const [compareModal, setCompareModal] = useState<{ visible: boolean; draft: string; final: string }>({
    visible: false,
    draft: '',
    final: '',
  });
  const [compareFeedbackOpen, setCompareFeedbackOpen] = useState<boolean>(false);
  const openModal = (title: string, content: string) => setOutputModal({ visible: true, title, content });
  const closeModal = () => setOutputModal({ visible: false, title: '', content: '' });
  const openCompare = (draft: string, finalText: string) =>
    setCompareModal({ visible: true, draft, final: finalText });
  const closeCompare = () => setCompareModal({ visible: false, draft: '', final: '' });

  useEffect(() => {
    (async () => {
      const { data } = await researchStorageService.getSession(sessionId);
      if (data) setSession(data);
    })();
  }, [sessionId]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons name="chevron-back" size={22} color={Colors.text.primary} />
        </TouchableOpacity>
        <Text style={styles.title} numberOfLines={1}>
          {session?.topic || 'Research'}
        </Text>
        <View style={{ width: 22 }} />
      </View>

      {!session ? (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={styles.content}>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Created:</Text>
            <Text style={styles.value}>
              {new Date(session.createdAt).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.label}>Status:</Text>
            <Text style={styles.value}>{session.status}</Text>
          </View>
          {session.metadata && (
            <View style={styles.infoRow}>
              <Text style={styles.label}>Runtime:</Text>
              <Text style={styles.value}>{session.metadata.duration?.toFixed(1)}s</Text>
            </View>
          )}

          {/* Flow (reconstructed from metadata) */}
          {!!session.metadata?.stepDetails?.length && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Flow</Text>
              <FlowDiagram
                steps={session.metadata.stepDetails}
                onStepPress={type => setExpandedStep(expandedStep === type ? null : type)}
              />

              {expandedStep && (
                <View style={styles.flowDetails}>
                  {expandedStep === 'planning' && session.questions?.length ? (
                    <View>
                      <Text style={styles.sectionTitle}>Research Questions</Text>
                      {session.questions.map((q, i) => (
                        <View key={i} style={{ marginBottom: 10 }}>
                          <Text style={styles.question}>{q.question}</Text>
                          <Text style={styles.muted}>{q.rationale}</Text>
                        </View>
                      ))}
                    </View>
                  ) : null}

                  {expandedStep === 'searching' && session.sources?.length ? (
                    <View>
                      <Text style={styles.sectionTitle}>Sources ({session.sources.length})</Text>
                      {session.sources.slice(0, 5).map((s, i) => (
                        <View key={i} style={{ marginBottom: 10 }}>
                          <Text style={styles.question}>{s.title}</Text>
                          <Text style={styles.muted}>{s.sourceName}</Text>
                        </View>
                      ))}
                      {session.sources.length > 5 && (
                        <TouchableOpacity
                          style={{ marginTop: 6 }}
                          onPress={() =>
                            openModal(
                              'All Sources (Searching)',
                              session.sources
                                .map(s => `• ${s.title}${s.sourceName ? ` (${s.sourceName})` : ''}\n${s.url}`)
                                .join('\n\n')
                            )
                          }
                        >
                          <Text style={styles.link}>View all ({session.sources.length})</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ) : null}

                  {expandedStep === 'extracting' && session.sources?.length ? (
                    <View>
                      <Text style={styles.sectionTitle}>Extracted Summaries</Text>
                      {session.sources.slice(0, 3).map((s, i) => (
                        <View key={i} style={{ marginBottom: 10 }}>
                          <Text style={styles.question}>{s.title}</Text>
                          <Text style={styles.muted}>{s.summary}</Text>
                        </View>
                      ))}
                      {session.sources.length > 3 && (
                        <TouchableOpacity
                          style={{ marginTop: 6 }}
                          onPress={() =>
                            openModal(
                              'All Extracted Summaries',
                              session.sources
                                .map(
                                  s =>
                                    `• ${s.title}${s.sourceName ? ` (${s.sourceName})` : ''}\n${s.summary}\n${s.url}`
                                )
                                .join('\n\n')
                            )
                          }
                        >
                          <Text style={styles.link}>View all ({session.sources.length})</Text>
                        </TouchableOpacity>
                      )}
                    </View>
                  ) : null}

                  {expandedStep === 'drafting' && (
                    <View>
                      <Text style={styles.sectionTitle}>Draft</Text>
                      <Text style={styles.muted}>
                        {String(
                          session.metadata?.stepDetails?.find(s => s.type === 'drafting')?.data
                            ?.outputPreview || ''
                        )}
                      </Text>
                      {(() => {
                        const citations =
                          ((session.metadata?.stepDetails?.find(s => s.type === 'drafting') as any)?.data
                            ?.citations || []) as string[];
                        if (!citations.length) return null;
                        const getHost = (url: string) => {
                          try {
                            return new URL(url).hostname.replace('www.', '');
                          } catch {
                            return '';
                          }
                        };
                        const mapped = citations.map(url => {
                          const host = getHost(url);
                          const match =
                            session.sources?.find(s => s.url === url) ||
                            session.sources?.find(s => getHost(s.url) === host);
                          return {
                            url,
                            title: match?.title || url,
                            sourceName: match?.sourceName || host,
                          };
                        });
                        return (
                          <View style={{ marginTop: 8 }}>
                            <Text style={styles.sectionTitle}>Sources used</Text>
                            {mapped.slice(0, 6).map((c, i) => (
                              <Text key={i} style={styles.muted}>
                                • {c.title} {c.sourceName ? `(${c.sourceName})` : ''}
                              </Text>
                            ))}
                          </View>
                        );
                      })()}
                    </View>
                  )}

                  {expandedStep === 'critiquing' && (
                    <View>
                      <Text style={styles.sectionTitle}>Critique</Text>
                      <Text style={styles.question}>
                        Overall: {String((session.metadata as any)?.critique?.overallQuality || '')}
                      </Text>
                      {((session.metadata as any)?.critique?.strengths || []).map(
                        (t: string, i: number) => (
                          <Text key={i} style={styles.muted}>
                            • {t}
                          </Text>
                        )
                      )}
                      {((session.metadata as any)?.critique?.weaknesses || []).map(
                        (t: string, i: number) => (
                          <Text key={i} style={styles.muted}>
                            • {t}
                          </Text>
                        )
                      )}
                      {((session.metadata as any)?.critique?.suggestions || []).map(
                        (t: string, i: number) => (
                          <Text key={i} style={styles.muted}>
                            • {t}
                          </Text>
                        )
                      )}
                      {((session.metadata as any)?.critique?.valueAdded || []).length ? (
                        <View style={{ marginTop: 6 }}>
                          <Text style={styles.sectionTitle}>Value Added</Text>
                          {((session.metadata as any)?.critique?.valueAdded || []).map(
                            (t: string, i: number) => (
                              <Text key={i} style={styles.muted}>
                                • {t}
                              </Text>
                            )
                          )}
                        </View>
                      ) : null}
                    </View>
                  )}

                  {expandedStep === 'refining' && (
                    <View>
                      <Text style={styles.sectionTitle}>Applied Feedback</Text>
                      {(session.metadata?.stepDetails?.find(s => s.type === 'refining') as any)?.data
                        ?.appliedSuggestions?.map((t: string, i: number) => (
                        <Text key={i} style={styles.muted}>
                          • {t}
                        </Text>
                      ))}
                      {(() => {
                        const citations =
                          ((session.metadata?.stepDetails?.find(s => s.type === 'refining') as any)?.data
                            ?.citations || []) as string[];
                        if (!citations.length) return null;
                        const getHost = (url: string) => {
                          try {
                            return new URL(url).hostname.replace('www.', '');
                          } catch {
                            return '';
                          }
                        };
                        const mapped = citations.map(url => {
                          const host = getHost(url);
                          const match =
                            session.sources?.find(s => s.url === url) ||
                            session.sources?.find(s => getHost(s.url) === host);
                          return {
                            url,
                            title: match?.title || url,
                            sourceName: match?.sourceName || host,
                          };
                        });
                        return (
                          <View style={{ marginTop: 8 }}>
                            <Text style={styles.sectionTitle}>Final Sources</Text>
                            {mapped.slice(0, 6).map((c, i) => (
                              <Text key={i} style={styles.muted}>
                                • {c.title} {c.sourceName ? `(${c.sourceName})` : ''}
                              </Text>
                            ))}
                          </View>
                        );
                      })()}
                    </View>
                  )}
                </View>
              )}
            </View>
          )}

          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Final Report</Text>
            <TouchableOpacity
              style={styles.compareButton}
              onPress={() => {
                const draftFull =
                  (session.metadata?.stepDetails?.find(s => s.type === 'drafting') as any)?.data?.draftFull ||
                  (session.metadata?.stepDetails?.find(s => s.type === 'drafting') as any)?.data?.outputPreview ||
                  '';
                openCompare(String(draftFull), session.finalReport || '');
              }}
            >
              <Ionicons name="swap-horizontal" size={16} color={Colors.background.primary} />
              <Text style={styles.compareText}>Compare Draft vs Final</Text>
            </TouchableOpacity>
            <ResearchReport report={session.finalReport || ''} metadata={session.metadata || undefined} />
          </View>
        </ScrollView>
      )}
      {/* Full view modal */}
      <Modal visible={outputModal.visible} animationType="slide" onRequestClose={closeModal} transparent={false}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Pressable onPress={closeModal} style={styles.modalClose}>
              <Ionicons name="close" size={24} color={Colors.text.primary} />
            </Pressable>
            <Text style={styles.modalTitle} numberOfLines={1}>{outputModal.title}</Text>
            <View style={{ width: 24 }} />
          </View>
          <ScrollView contentContainerStyle={styles.modalContent}>
            <MarkdownText text={outputModal.content} />
          </ScrollView>
        </SafeAreaView>
      </Modal>
      <Modal visible={compareModal.visible} animationType="slide" onRequestClose={closeCompare} transparent={false}>
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Pressable onPress={closeCompare} style={styles.modalClose}>
              <Ionicons name="close" size={24} color={Colors.text.primary} />
            </Pressable>
            <Text style={styles.modalTitle} numberOfLines={1}>Draft vs Final</Text>
            <View style={{ width: 24 }} />
          </View>
          <View style={styles.modalCompareRow}>
            <View style={styles.modalColumn}>
              <Text style={styles.modalColumnTitle}>Draft</Text>
              <ScrollColumn contentContainerStyle={styles.modalColumnContent}>
                <MarkdownText text={compareModal.draft || '—'} />
              </ScrollColumn>
            </View>
            <View style={styles.modalColumn}>
              <Text style={styles.modalColumnTitle}>Final</Text>
              <ScrollColumn contentContainerStyle={styles.modalColumnContent}>
                <MarkdownText text={compareModal.final || '—'} />
              </ScrollColumn>
            </View>
          </View>
          {/* Feedback & Changes (history) */}
          {(() => {
            const critique = (session?.metadata as any)?.critique || null;
            const refining = session?.metadata?.stepDetails?.find(s => s.type === 'refining') as any;
            const appliedSuggestions: string[] = (refining?.data?.appliedSuggestions || []) as string[];
            const appliedValueAdded: string[] = (refining?.data?.appliedValueAdded || []) as string[];
            if (!critique && appliedSuggestions.length === 0 && appliedValueAdded.length === 0) return null;
            const isApplied = (item: string) =>
              appliedSuggestions.some(s => s.toLowerCase() === item.toLowerCase());
            const isValueApplied = (item: string) =>
              appliedValueAdded.some(s => s.toLowerCase() === item.toLowerCase());
            return (
              <View style={styles.feedbackHeader}>
                <TouchableOpacity
                  style={styles.feedbackHeaderButton}
                  onPress={() => setCompareFeedbackOpen(prev => !prev)}
                  activeOpacity={0.7}
                >
                  <Ionicons
                    name={compareFeedbackOpen ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color={Colors.text.primary}
                  />
                  <Text style={styles.feedbackHeaderText}>Feedback & Changes</Text>
                </TouchableOpacity>
                {compareFeedbackOpen && (
                  <ScrollView contentContainerStyle={styles.modalContent}>
                    {critique?.overallQuality && (
                      <View style={styles.badgeRow}>
                        <View style={styles.badge}>
                          <Ionicons name="ribbon" size={14} color={Colors.accent.primary} />
                          <Text style={styles.badgeText}>Overall: {critique.overallQuality}</Text>
                        </View>
                      </View>
                    )}
                    {!!(critique?.strengths || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Strengths</Text>
                        {(critique.strengths as string[]).map((t, i) => (
                          <Text key={i} style={styles.modalText}>• {t}</Text>
                        ))}
                      </View>
                    )}
                    {!!(critique?.weaknesses || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Weaknesses</Text>
                        {(critique.weaknesses as string[]).map((t, i) => (
                          <Text key={i} style={styles.modalText}>• {t}</Text>
                        ))}
                      </View>
                    )}
                    {!!(critique?.suggestions || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Suggestions</Text>
                        {(critique.suggestions as string[]).map((t, i) => (
                          <View key={i} style={styles.changeRow}>
                            <Ionicons
                              name={isApplied(t) ? 'checkmark-circle' : 'ellipse-outline'}
                              size={16}
                              color={isApplied(t) ? Colors.status.success : Colors.text.tertiary}
                            />
                            <Text style={styles.modalText}>{t}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                    {!!(critique?.valueAdded || []).length && (
                      <View style={{ marginTop: 6 }}>
                        <Text style={styles.feedbackSectionTitle}>Value Added</Text>
                        {(critique.valueAdded as string[]).map((t, i) => (
                          <View key={i} style={styles.changeRow}>
                            <Ionicons
                              name={isValueApplied(t) ? 'checkmark-circle' : 'ellipse-outline'}
                              size={16}
                              color={isValueApplied(t) ? Colors.status.success : Colors.text.tertiary}
                            />
                            <Text style={styles.modalText}>{t}</Text>
                          </View>
                        ))}
                      </View>
                    )}
                  </ScrollView>
                )}
              </View>
            );
          })()}
        </SafeAreaView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 4,
    marginRight: 8,
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
    color: Colors.text.primary,
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: Colors.text.secondary,
  },
  content: {
    padding: 16,
    gap: 16,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  label: {
    fontSize: 13,
    color: Colors.text.tertiary,
  },
  value: {
    fontSize: 13,
    color: Colors.text.primary,
    fontWeight: '600',
  },
  section: {
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  link: {
    fontSize: 13,
    color: Colors.accent.primary,
    fontWeight: '600',
  },
  flowDetails: {
    marginTop: 8,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    gap: 8,
  },
  question: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  muted: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  // Modal
  modalContainer: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  modalHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  modalClose: {
    padding: 8,
    marginRight: 8,
  },
  modalTitle: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    textAlign: 'center',
  },
  modalContent: {
    padding: 16,
  },
  modalText: {
    fontSize: 15,
    lineHeight: 22,
    color: Colors.text.secondary,
  },
  feedbackTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 6,
  },
  feedbackSectionTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    marginBottom: 4,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    marginBottom: 4,
  },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: Colors.background.secondary,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    alignSelf: 'flex-start',
  },
  badgeText: {
    fontSize: 12,
    color: Colors.text.secondary,
    fontWeight: '700',
  },
  changeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  modalCompareRow: {
    flex: 1,
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  modalColumn: {
    flex: 1,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    overflow: 'hidden',
  },
  modalColumnTitle: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontWeight: '700',
    color: Colors.text.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  modalColumnContent: {
    padding: 12,
  },
  compareButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: Colors.accent.primary,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    gap: 6,
  },
  compareText: {
    fontSize: 13,
    fontWeight: '700',
    color: Colors.background.primary,
  },
});


